import { interval as observableInterval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { Component, Inject, OnDestroy, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, Validators, FormGroup, AbstractControl } from '@angular/forms';
import { AuthService, IAuthResponse as AuthResponse } from './../auth/auth.service';

import { NgbModal, NgbModalRef, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { NgbdBankIdModal } from '../modal/bank-id.modal.component';
import { NgbdOtpModal } from '../modal/otp.modal.component';
import { ErrorModalService } from '../modal/error.modal.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { personalNumberValidator } from '../_validators/personal-number.validator';
import { ParamMap, ActivatedRoute, Router } from '@angular/router';
import { Instance } from '../interfaces/instance.interface';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SessionService } from '../session/session.service';
import { NgbdOtpValidateModal } from '../modal/otp-validate.modal.component';
import { PortalMode } from '../enum/portalmode.enum'

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent implements OnDestroy {
  private qrExpiresIn = 300;
  private qrRemaining: number;
  private qrCountdownInterval: any;
  private orderRef: string;
  private lockedWindow: NgbModalRef;
  private collectionLoop: Subscription;
  private ongoingTransaction: boolean;

  public results: string;
  public errorMessage: string;
  public showOtp: boolean;
  public loginWithMobileBankIdForm: FormGroup;
  public loginWithOtpForm: FormGroup;
  public registrationCode: string;
  public loginMode: PortalMode = PortalMode.Default;

  public get isCreditor(): boolean { return this.loginMode === PortalMode.Borgenar }
  public get personal_number_otp(): AbstractControl { return this.loginWithOtpForm.get('personal_number_otp'); }
  public get remember_personal_number_otp(): AbstractControl { return this.loginWithOtpForm.get('remember_personal_number_otp'); }
  public get isMobile(): boolean { return this.deviceDetector.isMobile(); }

  constructor(
    protected auth: AuthService,
    protected sessionService: SessionService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected modalService: NgbModal,
    protected errorModalService: ErrorModalService,
    protected fb: FormBuilder,
    protected spinner: NgxSpinnerService,
    protected deviceDetector: DeviceDetectorService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject('BANKID_INTERVAL') protected bankIdInterval: number) {

    if (window.location.hostname.includes('borgenarsportalen') && !this.route.snapshot.data['loginMode']) {
      this.router.navigate(['/borgenar']);
      return;
    }

    this.registrationCode = this.route.snapshot.queryParams.code;
    this.loginMode = (this.route.snapshot.data['loginMode'] as any) ??
      (window.location.hostname.includes('borgenarsportalen') ? PortalMode.Borgenar : PortalMode.Default);

    const personalNumber = this.sessionService.getPersonalNumber();

    if (this.isCreditor) {
      this.showOtp = false;
    }
    this.loginWithOtpForm = this.fb.group({
      'personal_number_otp': new FormControl(
        personalNumber,
        [
          personalNumberValidator
        ]),
      'remember_personal_number_otp': new FormControl(!!personalNumber)
    });

    this.auth.isLoggedIn().subscribe(
      isLoggedIn => {
        if (isLoggedIn) {
          if (this.registrationCode)
            this.auth.logoutFromCodeLink();
          else if (this.loginMode === PortalMode.Borgenar && this.sessionService.getPortalMode() !== PortalMode.Borgenar) {
            this.sessionService.logout();
          }
          else if (this.loginMode !== PortalMode.Borgenar && this.sessionService.getPortalMode() === PortalMode.Borgenar) {
            this.sessionService.logout();
          }
          else if (this.sessionService.getPortalMode() === PortalMode.Borgenar) {
            this.redirectBorgenarAfterLogin();
            return;
          }
          else
            this.autoRoute();
        }
      },
      error => { });
  }

  public openModal(content: any, windowClass?: NgbModalOptions) {
    if (this.lockedWindow) {
      this.lockedWindow.close();
    }

    this.lockedWindow = this.modalService.open(content, windowClass);

    this.lockedWindow.result.then(() => {
      this.cancelBankId();
    });
  }

  private redirectBorgenarAfterLogin(): void {
    const pendingToken = sessionStorage.getItem('pendingGroupInviteToken');
    if (pendingToken) {
      this.router.navigate(['/borgenar/group/accept', pendingToken]);
    } else {
      this.router.navigate(['/borgenar/start']);
    }
  }

  private autoRoute() {
    const portalMode = this.sessionService.getPortalMode();
    if (portalMode === PortalMode.Borgenar) {
      this.redirectBorgenarAfterLogin();
      return;
    }

    this.route.queryParamMap.subscribe(
      (params: ParamMap) => {
        const navigateTo = params.get('navigate-to');
        const instanceId = params.get('instance');
        const roleType = params.get('role');
        if (navigateTo && instanceId && roleType) {
          this.spinner.show();
          this.sessionService.getInstances().subscribe(
            instanceSelector => {
              const instance: Instance = instanceSelector.instances.find(i =>
                i.id === instanceId &&
                i.roleType === roleType);
              if (instance) {
                this.sessionService.setInstance(instance, navigateTo);
              } else {
                this.sessionService.selectInstance();
              }
            }, error => {
              if (location.pathname.includes('borgenar'))
                this.router.navigate(['/borgenar']);
              else
                this.router.navigate(['/home']);
            }, () => {
              this.spinner.hide();
            });
        } else {
          this.sessionService.setInstance(null, '/select-instance');
        }
      });
  }

  private openBankIdModal() {
    this.openModal(NgbdBankIdModal, { backdrop: 'static', keyboard: false, centered: true });
    if (this.lockedWindow && this.lockedWindow.componentInstance) {
      this.lockedWindow.componentInstance.qrRemaining = this.qrRemaining;
      this.lockedWindow.componentInstance.extendTimeoutClicked.subscribe(() => {
        this.extendQrCode();
      });
    }
  }

  public extendQrCode() {
    this.startTotalCountdown();
    this.startNewBankIdSession();
  }

  public openErrorModal(title: string, message: string) {
    this.errorModalService.open(title, message, () => { }, this.lockedWindow);
  }

  private inloggningsfel(meddelande: string) {
    this.openErrorModal('Inloggningsfel', meddelande);
    this.sessionService.logout();
    this.spinner.hide();
  };

  public loginBankIdOnFile() {
    this.spinner.show();
    const auth$ = this.isCreditor ? this.auth.authBankIdCreditor() : this.auth.authBankId();
    auth$.subscribe(
      authResponse => {
        this.spinner.hide();
        this.openBankIdModal();
        this.lockedWindow.componentInstance.isMobileBankId = false;
        this.ongoingTransaction = true;
        this.startBankIdClient(authResponse.autoStartToken);
        this.orderRef = authResponse.orderRef;
        this.collectLoop();
      },
      error => this.inloggningsfel(error.error.exceptionMessage));
  }

  public loginMobileBankId() {
    this.spinner.show();
    this.openBankIdModal();

    if (this.lockedWindow && this.lockedWindow.componentInstance) {
      this.lockedWindow.componentInstance.isMobileBankId = true;
      this.lockedWindow.componentInstance.showQrCode = !this.deviceDetector.isMobile();
    }

    this.startTotalCountdown();
    this.startNewBankIdSession();

    this.spinner.hide();
  }

  private startNewBankIdSession() {
    const auth$ = this.isCreditor ? this.auth.authMobileBankIdCreditor() : this.auth.authMobileBankId();
    auth$.subscribe(
      authResponse => {
        this.orderRef = authResponse.orderRef;
        this.unsubscribeCollectionLoop();
        this.ongoingTransaction = true;

        if (this.lockedWindow && this.lockedWindow.componentInstance) {
          this.lockedWindow.componentInstance.hintCode = null;
          this.lockedWindow.componentInstance.status = null;
          this.lockedWindow.componentInstance.qrCode = authResponse.qrCode;
        }

        this.collectLoop();

        if (this.deviceDetector.isMobile() && this.lockedWindow && this.lockedWindow.componentInstance) {
          this.startBankIdClient(authResponse.autoStartToken);
        }
      },
      error => {
        this.inloggningsfel(error.error.exceptionMessage);
        if (this.lockedWindow) {
          this.lockedWindow.close();
        }
      }
    );
  }

  private startTotalCountdown() {
    this.qrRemaining = this.qrExpiresIn;
    if (this.qrCountdownInterval) {
      clearInterval(this.qrCountdownInterval);
    }

    this.qrCountdownInterval = setInterval(() => {
      this.qrRemaining--;
      if (this.lockedWindow && this.lockedWindow.componentInstance) {
        this.lockedWindow.componentInstance.qrRemaining = this.qrRemaining;
      }

      if (this.qrRemaining <= 0) {
        clearInterval(this.qrCountdownInterval);

        if (this.lockedWindow && this.lockedWindow.componentInstance) {
          this.lockedWindow.componentInstance.hintCode = 'expiredTransaction';
          this.lockedWindow.componentInstance.status = 'failed';
          this.lockedWindow.componentInstance.showBankIdLink = true;
          this.ongoingTransaction = false;
          this.unsubscribeCollectionLoop();
        }
      }
    }, 1000);
  }

  public openOtpModal() {
    this.spinner.show();
    this.auth.getOtpContactInfo(this.personal_number_otp.value).subscribe(
      kontaktinfos => {
        this.openModal(NgbdOtpModal, { backdrop: 'static', keyboard: false, centered: true });
        this.spinner.hide();
        this.lockedWindow.componentInstance.kontaktinfos = kontaktinfos;
        this.lockedWindow.result
          .then(
            result => {
              if (!result) {
                return;
              }
              this.spinner.show();
              this.auth.sendOtp(this.personal_number_otp.value, result, this.sessionService.getLanguage()).subscribe(
                result => {
                  const requestId: string = result.requestId;
                  this.openModal(NgbdOtpValidateModal, { backdrop: 'static', keyboard: false, centered: true });
                  this.spinner.hide();
                  this.lockedWindow.componentInstance.requestId = requestId;
                  this.lockedWindow.result
                    .then(result => {
                      if (!result) {
                        return;
                      }
                      this.spinner.show();
                      this.auth.authOtp(requestId, result.otp).subscribe(
                        result => {
                          this.auth.setToken(result.accessToken);
                          this.sessionService.setLoginMethod('otp');
                          this.sessionService.setPersonalNumber(
                            this.remember_personal_number_otp.value
                              ? this.personal_number_otp.value
                              : null);
                          this.autoRoute();
                          this.spinner.hide();
                        },
                        exception => this.inloggningsfel(exception.error.message));
                    });
                },
                exception => this.inloggningsfel(exception.error.message));
            });
      },
      exception => this.inloggningsfel(exception.error.message));
  }

  public toggleShowOtp() {
    if (this.isCreditor) {
      return;
    }
    this.showOtp = !this.showOtp;
  }

  private collectLoop() {
    let lastCollectHandled = true;
    this.collectionLoop = observableInterval(this.bankIdInterval).pipe(
      takeWhile(() => this.ongoingTransaction)).subscribe(
        () => {
          if (!lastCollectHandled) {
            return;
          }
          lastCollectHandled = false;

          this.auth.collectBankId(this.orderRef, this.registrationCode).subscribe(
            collectResponse => {
              if (collectResponse.status === 'failed'
                && this.lockedWindow?.componentInstance?.isMobileBankId === true
                && (collectResponse.hintCode === 'startFailed' || collectResponse.hintCode === 'expiredTransaction')
                && this.qrRemaining > 0) {
                this.unsubscribeCollectionLoop();
                this.startNewBankIdSession();
                return;
              }

              lastCollectHandled = true;
              if (this.lockedWindow && this.lockedWindow.componentInstance) {
                this.lockedWindow.componentInstance.hintCode = collectResponse.hintCode;
                this.lockedWindow.componentInstance.status = collectResponse.status;
                if (this.lockedWindow.componentInstance.isMobileBankId && collectResponse.qrCode) {
                  this.lockedWindow.componentInstance.qrCode = collectResponse.qrCode;
                }
              }

              if (collectResponse.status === 'complete') {
                if (collectResponse.completionData.accessToken) {
                  const loginMethod: string = this.lockedWindow.componentInstance.isMobileBankId
                    ? this.isMobile ? 'mobile-pin' : 'mobile-qr' : 'pc';
                  this.auth.setToken(collectResponse.completionData.accessToken);
                  this.sessionService.setLoginMethod(loginMethod);
                  this.sessionService.setPortalMode(this.loginMode);
                  const portalMode = this.sessionService.getPortalMode();
                  if (portalMode === PortalMode.Borgenar) {
                    this.redirectBorgenarAfterLogin();
                  } else {
                    this.autoRoute();
                  }
                }

                this.lockedWindow.close();
              }

              this.ongoingTransaction = collectResponse.status === 'pending';
            },
            error => this.inloggningsfel(error.error.exceptionMessage));
        },
        () => this.ongoingTransaction = false);
  }

  private cancelBankId() {
    if (this.ongoingTransaction) {
      this.auth.cancelBankId(this.orderRef).subscribe(() => this.unsubscribeCollectionLoop());
    } else {
      this.unsubscribeCollectionLoop();
    }
    if (this.qrCountdownInterval) {
      clearInterval(this.qrCountdownInterval);
      this.qrCountdownInterval = null;
    }
  }

  private unsubscribeCollectionLoop() {
    if (!!this.collectionLoop && !this.collectionLoop.closed) {
      this.collectionLoop.unsubscribe();
    }
  }

  private startBankIdClient(token: string, returnUrl: string = null) {
    this.document.location.href = `bankid:///?autostarttoken=${token}&redirect=${returnUrl ? returnUrl : ''}`;
  }

  ngOnDestroy() {
    this.cancelBankId();
  }
}
